import { Spin } from "antd";
import {
  StatsigClient,
  type StatsigOptions,
  type StatsigUser,
} from "@statsig/js-client";
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import {
  SELF_SERVE_CAMPAIGN_CREATION_AND_ACTIVATION_FLOW_GATE,
  STATSIG_CLIENT_KEY,
  STATSIG_ENV_TIER,
} from "services/featureFlags/constants";
import { normalizeStatsigEnvironmentTier } from "services/featureFlags/statsigHelpers";
import { getSelfCampaignQueryParams } from "./queryParams";

type SelfCampaignGuardProps = {
  children: React.ReactNode;
  enabledRedirectTo?: string;
  disabledRedirectTo?: string;
};

const SELF_SERVE_ANON_USER_ID_STORAGE_KEY = "self_serve_campaign_statsig_id";

type GateStatus = "loading" | "enabled" | "disabled";

const getStableAnonymousUserId = (): string => {
  const existing = localStorage.getItem(SELF_SERVE_ANON_USER_ID_STORAGE_KEY);
  if (existing) {
    return existing;
  }

  const generatedId =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? `anon_${crypto.randomUUID()}`
      : `anon_${Date.now()}_${Math.random().toString(36).slice(2)}`;

  localStorage.setItem(SELF_SERVE_ANON_USER_ID_STORAGE_KEY, generatedId);
  return generatedId;
};

const SelfCampaignGuard: React.FC<SelfCampaignGuardProps> = ({
  children,
  enabledRedirectTo,
  disabledRedirectTo,
}) => {
  const location = useLocation();
  const [gateStatus, setGateStatus] = useState<GateStatus>("loading");

  useEffect(() => {
    let isMounted = true;
    let statsigClient: StatsigClient | null = null;

    const resolveGate = async () => {
      if (!STATSIG_CLIENT_KEY) {
        if (isMounted) {
          setGateStatus("disabled");
        }
        return;
      }

      const queryParams = getSelfCampaignQueryParams(location.search);
      const anonymousUserId = queryParams.email || getStableAnonymousUserId();
      const statsigUser: StatsigUser = {
        userID: anonymousUserId,
        email: queryParams.email,
      };
      const statsigTier = normalizeStatsigEnvironmentTier(STATSIG_ENV_TIER);
      const statsigOptions: StatsigOptions | undefined = statsigTier
        ? { environment: { tier: statsigTier } }
        : undefined;

      try {
        statsigClient = new StatsigClient(
          STATSIG_CLIENT_KEY,
          statsigUser,
          statsigOptions,
        );
        await statsigClient.initializeAsync();

        if (!isMounted) {
          return;
        }

        const gateEnabled = statsigClient.checkGate(
          SELF_SERVE_CAMPAIGN_CREATION_AND_ACTIVATION_FLOW_GATE,
        );
        setGateStatus(gateEnabled ? "enabled" : "disabled");
      } catch (_error) {
        if (isMounted) {
          setGateStatus("disabled");
        }
      }
    };

    setGateStatus("loading");
    resolveGate();

    return () => {
      isMounted = false;
      if (statsigClient) {
        void statsigClient.shutdown();
      }
    };
  }, [location.search]);

  if (gateStatus === "loading") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fdfdfb",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (gateStatus === "disabled" && disabledRedirectTo) {
    return (
      <Navigate
        to={{
          pathname: disabledRedirectTo,
          search: location.search,
        }}
        replace
      />
    );
  }

  if (gateStatus === "enabled" && enabledRedirectTo) {
    return (
      <Navigate
        to={{
          pathname: enabledRedirectTo,
          search: location.search,
        }}
        replace
      />
    );
  }

  return <>{children}</>;
};

export default SelfCampaignGuard;
